/**
 * /admin/communities/new — full-page community creation (same layout as the
 * settings page, so create and edit read as the same surface).
 *
 * Everything here is pending until Create: the identity hero previews the
 * unsaved form live, tier grants and hand-picked people are collected locally,
 * and one atomic RPC creates the community + chat + default channel + members.
 * On success we land on the new community's settings page.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  useCommunityTiersForHub,
  useCreateCustomCommunity,
  useSearchCommunityUsers,
} from '@/api/community-hooks';
import type { ChannelMember } from '@/api/community';
import { getChannelIcon } from '@/lib/channel-icons';
import LucideIconPicker from '@/components/community/LucideIconPicker';
import { AddPeopleField } from '@/components/community/AddPeopleField';
import {
  ColorSwatchRow,
  COMMUNITY_COLORS,
  summariseMembership,
  TierRow,
} from '@/components/community/community-form';

export const Route = createFileRoute('/admin/communities_/new')({
  component: CreateCommunityPage,
});

function CreateCommunityPage() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [color, setColor] = useState<string>(COMMUNITY_COLORS[0]);
  const [selectedTierIds, setSelectedTierIds] = useState<string[]>([]);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [selectedPeople, setSelectedPeople] = useState<Map<string, ChannelMember>>(
    new Map(),
  );

  const { data: tiers = [] } = useCommunityTiersForHub();
  const { data: peopleResults = [], isFetching: peopleSearching } =
    useSearchCommunityUsers(peopleQuery);
  const create = useCreateCustomCommunity();

  const candidates = peopleResults.filter((p) => !selectedPeople.has(p.user_id));

  const membershipSummary = useMemo(
    () => summariseMembership(tiers, selectedTierIds, selectedPeople.size),
    [tiers, selectedTierIds, selectedPeople.size],
  );

  const toggleTier = (tierId: string) => {
    setSelectedTierIds((prev) =>
      prev.includes(tierId) ? prev.filter((id) => id !== tierId) : [...prev, tierId],
    );
  };

  const handleCreate = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      toast.error('Community name is required');
      return;
    }
    create.mutate(
      {
        name: trimmed,
        description: description.trim() || null,
        icon: icon ?? null,
        color,
        tierIds: selectedTierIds.length > 0 ? selectedTierIds : undefined,
        memberIds:
          selectedPeople.size > 0 ? Array.from(selectedPeople.keys()) : undefined,
      },
      {
        onSuccess: (communityId) => {
          toast.success(`"${trimmed}" created`);
          navigate({
            to: '/admin/communities/$communityId',
            params: { communityId },
          });
        },
        onError: (err) => {
          toast.error(err?.message ?? 'Failed to create community');
        },
      },
    );
  };

  const HeroIcon = getChannelIcon(icon)?.Component ?? Users;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Sticky header: back link + the one committing action */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 h-14 border-b border-border flex-shrink-0 bg-background/95 backdrop-blur">
        <Link
          to="/admin/communities"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors min-w-0"
        >
          <ArrowLeft className="w-4 h-4 flex-shrink-0" aria-hidden />
          <span className="truncate">Communities</span>
        </Link>
        <Button
          size="sm"
          onClick={handleCreate}
          disabled={create.isPending || !name.trim()}
        >
          {create.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
          ) : (
            <Plus className="w-4 h-4" aria-hidden />
          )}
          Create community
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-4 sm:p-6 max-w-3xl space-y-5">
          {/* Identity hero — live preview of the unsaved form */}
          <div className="flex items-center gap-4">
            <span
              className="flex w-14 h-14 items-center justify-center rounded-xl text-white flex-shrink-0"
              style={{ backgroundColor: color }}
            >
              <HeroIcon className="w-7 h-7" aria-hidden />
            </span>
            <div className="min-w-0">
              <h1
                className={`text-lg font-semibold truncate ${
                  name.trim() ? '' : 'text-muted-foreground/60'
                }`}
              >
                {name.trim() || 'New community'}
              </h1>
              <p className="text-sm text-muted-foreground">{membershipSummary}</p>
            </div>
          </div>

          {/* Profile */}
          <section className="space-y-2.5">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                Profile
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                A standalone community with its own channels, chat, and members —
                it belongs to no tier.
              </p>
            </div>
            <div className="rounded-xl border border-border p-4 space-y-4">
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="new-community-name">Name</Label>
                  <Input
                    id="new-community-name"
                    value={name}
                    maxLength={100}
                    placeholder="e.g. Platinum"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setName(e.target.value)
                    }
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Icon</Label>
                  <LucideIconPicker value={icon} onChange={setIcon} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Accent color</Label>
                <ColorSwatchRow value={color} onChange={setColor} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-community-description">Description</Label>
                <Textarea
                  id="new-community-description"
                  value={description}
                  maxLength={500}
                  rows={2}
                  placeholder="What this community is about (optional)"
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setDescription(e.target.value)
                  }
                />
              </div>
            </div>
          </section>

          {/* Tier access — applied when the community is created */}
          <section className="space-y-2.5">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                Tier access
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Members of granted tiers join when the community is created; new
                members of those tiers join automatically. You can change this
                later.
              </p>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="rounded-lg border border-border divide-y divide-border overflow-hidden">
                {tiers.map((tier) => (
                  <TierRow
                    key={tier.id}
                    tier={tier}
                    checked={selectedTierIds.includes(tier.id)}
                    onToggle={() => toggleTier(tier.id)}
                  />
                ))}
                {tiers.length === 0 && (
                  <p className="p-4 text-center text-xs text-muted-foreground">
                    No tiers available
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Members — collected locally, added by the create RPC */}
          <section className="space-y-2.5">
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
              Members
            </p>
            <div className="rounded-xl border border-border p-4">
              <AddPeopleField
                description="Hand-picked members keep access even if a tier is revoked later."
                chips={Array.from(selectedPeople.values())}
                onRemoveChip={(person) =>
                  setSelectedPeople((prev) => {
                    const next = new Map(prev);
                    next.delete(person.user_id);
                    return next;
                  })
                }
                query={peopleQuery}
                onQueryChange={setPeopleQuery}
                results={candidates}
                searching={peopleSearching}
                onPick={(person) => {
                  setSelectedPeople((prev) =>
                    new Map(prev).set(person.user_id, person),
                  );
                  setPeopleQuery('');
                }}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
